import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, MousePointerClick, Eye, Zap, Globe,
  RefreshCw, ArrowUpRight, ArrowDownRight, Lightbulb, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, FunnelChart, Funnel, LabelList, Cell
} from 'recharts';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';



const FUNNEL_COLORS = ['#D4AF37', '#4FB3D9', '#3CCB8E', '#F0B35A'];

export default function AnalyticsPage() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [overview, setOverview] = useState(null);
  const [ctrData, setCtrData] = useState([]);
  const [ctrDim, setCtrDim] = useState('site_id');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const siteParam = selectedSite !== 'all' ? selectedSite : undefined;
      const [sitesRes, overviewRes, ctrRes, suggestionsRes] = await Promise.all([
        api.get('/sites'),
        api.get('/analytics/overview', { params: { site_id: siteParam, days } }),
        api.get('/analytics/ctr', { params: { dimension: ctrDim, site_id: siteParam, days } }),
        api.get('/analytics/suggestions', { params: { site_id: siteParam } }),
      ]);
      setSites(sitesRes.data);
      setOverview(overviewRes.data);
      setCtrData(ctrRes.data.data || []);
      setSuggestions(suggestionsRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedSite, ctrDim, days]);

  useEffect(() => { loadData(); }, [loadData]);

  const getSiteName = (id) => sites.find(s => s.id === id)?.brand_name || id?.slice(0, 8);

  const events = overview?.events || {};
  const funnel = overview?.funnel?.funnel || [];
  const daily = overview?.daily_trend || [];
  const revenue = overview?.revenue || {};

  if (loading) return <div className="space-y-4">{[1,2,3,4].map(i => <div key={i} className="h-24 rounded-xl shimmer" />)}</div>;

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">Analitik Paneli</h1>
          <p className="text-sm text-muted-foreground mt-1">Performans & donusum metrikleri</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-[220px] bg-secondary/40 border-border/70"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tum Siteler</SelectItem>
              {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.brand_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[120px] bg-secondary/40 border-border/70"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 Gun</SelectItem>
              <SelectItem value="30">30 Gun</SelectItem>
              <SelectItem value="90">90 Gun</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadData} variant="ghost" size="icon"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          { label: 'Sayfa Goruntulenme', value: events.page_view || 0, icon: Eye },
          { label: 'CTA Tiklama', value: events.cta_click || 0, icon: MousePointerClick },
          { label: 'Affiliate Tiklama', value: events.affiliate_click || 0, icon: Zap },
          { label: 'Redirect Basarili', value: events.redirect_success || 0, icon: TrendingUp },
          { label: 'Toplam Gelir', value: `$${revenue.total_revenue || 0}`, icon: BarChart3 },
          { label: 'EPC', value: `$${revenue.epc || 0}`, icon: Globe },
        ].map((kpi, i) => (
          <Card key={i} className="relative border-border/70 overflow-hidden"><div className="kpi-accent" />
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
              <p className="text-xl font-semibold mt-1 font-['Space_Grotesk']">{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Daily Trend */}
        <Card className="xl:col-span-8 border-border/70">
          <CardHeader className="pb-2"><CardTitle className="text-base">Gunluk Etkinlik Trendi</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily}>
                  <defs><linearGradient id="goldG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4AF37" stopOpacity={0.15} /><stop offset="100%" stopColor="#D4AF37" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="rgba(167,176,190,0.1)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: '#A7B0BE', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#A7B0BE', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#12151A', border: '1px solid #262C35', borderRadius: 8, color: '#F2F4F7' }} />
                  <Area type="monotone" dataKey="count" stroke="#D4AF37" strokeWidth={2} fill="url(#goldG)" name="Etkinlik" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Funnel */}
        <Card className="xl:col-span-4 border-border/70">
          <CardHeader className="pb-2"><CardTitle className="text-base">Donusum Hunisi</CardTitle></CardHeader>
          <CardContent>
            {funnel.length > 0 ? (
              <div className="space-y-3">
                {funnel.map((step, i) => {
                  const maxCount = funnel[0]?.count || 1;
                  const width = Math.max(20, (step.count / maxCount) * 100);
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">{step.label}</span>
                        <span className="font-medium">{step.count}</span>
                      </div>
                      <div className="h-6 bg-secondary/30 rounded-md overflow-hidden">
                        <div className="h-full rounded-md transition-all duration-500" style={{ width: `${width}%`, background: FUNNEL_COLORS[i] || '#D4AF37' }} />
                      </div>
                      {step.drop_off_pct > 0 && (
                        <p className="text-[10px] text-red-400 mt-0.5 flex items-center gap-1">
                          <ArrowDownRight className="w-3 h-3" /> %{step.drop_off_pct} dusus
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4">Henuz veri yok</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* CTR Table */}
      <Card className="border-border/70">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">CTR Performansi</CardTitle>
          <Select value={ctrDim} onValueChange={setCtrDim}>
            <SelectTrigger className="w-[160px] bg-secondary/40 border-border/70"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="site_id">Site Bazli</SelectItem>
              <SelectItem value="page_slug">Sayfa Bazli</SelectItem>
              <SelectItem value="country">Ulke Bazli</SelectItem>
              <SelectItem value="device">Cihaz Bazli</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {ctrData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Henuz CTR verisi yok</p>
          ) : (
            <div className="space-y-2">
              {ctrData.slice(0, 10).map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/20 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-muted-foreground w-6">{i + 1}</span>
                    <span className="text-sm font-medium">{ctrDim === 'site_id' ? getSiteName(item.value) : item.value}</span>
                  </div>
                  <div className="flex items-center gap-6 text-xs">
                    <span className="text-muted-foreground">{item.views} goruntulenme</span>
                    <span className="text-muted-foreground">{item.clicks} tiklama</span>
                    <span className={`font-medium px-2 py-0.5 rounded ${item.ctr >= 5 ? 'text-emerald-400 bg-emerald-400/10' : item.ctr >= 2 ? 'text-amber-400 bg-amber-400/10' : 'text-red-400 bg-red-400/10'}`}>
                      %{item.ctr}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Smart Suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-border/70">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Lightbulb className="w-4 h-4 text-[hsl(var(--primary))]" /> Akilli Optimizasyon Onerileri</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {suggestions.map((s, i) => (
                <div key={i} className={`p-4 rounded-lg border-l-2 ${s.severity === 'critical' ? 'border-l-red-400 bg-red-400/5' : s.severity === 'warning' ? 'border-l-amber-400 bg-amber-400/5' : 'border-l-blue-400 bg-blue-400/5'}`}>
                  <div className="flex items-start justify-between">
                    <p className="text-sm font-medium">{s.title}</p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary/60 text-muted-foreground">{s.metric}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">{s.message}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
